import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ClientContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      if (response.data.length > 0 && !selectedClient) {
        setSelectedClient(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const addClient = async (clientData) => {
    try {
      const response = await axios.post(`${API}/clients`, clientData);
      await fetchClients();
      return response.data;
    } catch (error) {
      console.error('Error adding client:', error);
      throw error;
    }
  };

  const updateClient = async (clientId, patch) => {
    const response = await axios.put(`${API}/clients/${clientId}`, patch);
    // Re-fetch to refresh list + selected
    await fetchClients();
    // Keep selection on the edited client if it was selected
    if (selectedClient && selectedClient.id === clientId) {
      setSelectedClient(response.data);
    }
    return response.data;
  };

  const deleteClient = async (clientId, cascade = true) => {
    const response = await axios.delete(`${API}/clients/${clientId}`, { params: { cascade } });
    // If we deleted the selected client, switch to the first remaining one
    const fresh = await axios.get(`${API}/clients`);
    setClients(fresh.data);
    if (selectedClient && selectedClient.id === clientId) {
      setSelectedClient(fresh.data[0] || null);
    }
    return response.data;
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <ClientContext.Provider value={{
      clients,
      selectedClient,
      setSelectedClient,
      loading,
      fetchClients,
      addClient,
      updateClient,
      deleteClient,
      API
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within ClientProvider');
  }
  return context;
};